import { useEffect, useMemo } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, FontSize } from "./correspondenceFontSize";

const FONT_SIZES = ["10px", "11px", "12px", "14px", "16px"] as const;

function hasArabic(text: string): boolean {
  return /[\u0600-\u06FF]/.test(text);
}

type Labels = {
  bold: string;
  italic: string;
  underline: string;
  alignLeft: string;
  alignCenter: string;
  alignRight: string;
  alignJustify: string;
  bulletList: string;
  orderedList: string;
  undo: string;
  redo: string;
  fontSize: string;
};

type Props = {
  valueHtml: string;
  onChange: (html: string, plain: string) => void;
  disabled?: boolean;
  labels: Labels;
  invalid?: boolean;
};

export function CorrespondenceBodyEditor({
  valueHtml,
  onChange,
  disabled,
  labels,
  invalid,
}: Props) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      TextStyle,
      FontSize,
      TextAlign.configure({
        types: ["paragraph", "listItem"],
        alignments: ["left", "center", "right", "justify"],
      }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: valueHtml || "<p></p>",
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const plain = ed.getText({ blockSeparator: "\n\n" }).trim();
      onChange(html, plain);
      const rtl = hasArabic(plain);
      ed.view.dom.setAttribute("dir", rtl ? "rtl" : "ltr");
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = valueHtml || "<p></p>";
    if (current !== next && !editor.isFocused) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, valueHtml]);

  if (!editor) return null;

  const btn = (
    active: boolean,
    onClick: () => void,
    title: string,
    children: string,
  ) => (
    <button
      type="button"
      className={`dn-corr-rte-btn${active ? " is-active" : ""}`}
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );

  return (
    <div className={`dn-corr-rte${invalid ? " is-invalid" : ""}`}>
      <div className="dn-corr-rte-toolbar" role="toolbar" aria-label="Formatting">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), labels.bold, "B")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), labels.italic, "I")}
        {btn(
          editor.isActive("underline"),
          () => editor.chain().focus().toggleUnderline().run(),
          labels.underline,
          "U",
        )}
        <span className="dn-corr-rte-sep" aria-hidden="true" />
        {btn(
          editor.isActive({ textAlign: "left" }),
          () => editor.chain().focus().setTextAlign("left").run(),
          labels.alignLeft,
          "≡L",
        )}
        {btn(
          editor.isActive({ textAlign: "center" }),
          () => editor.chain().focus().setTextAlign("center").run(),
          labels.alignCenter,
          "≡C",
        )}
        {btn(
          editor.isActive({ textAlign: "right" }),
          () => editor.chain().focus().setTextAlign("right").run(),
          labels.alignRight,
          "≡R",
        )}
        {btn(
          editor.isActive({ textAlign: "justify" }),
          () => editor.chain().focus().setTextAlign("justify").run(),
          labels.alignJustify,
          "≡J",
        )}
        <span className="dn-corr-rte-sep" aria-hidden="true" />
        {btn(
          editor.isActive("bulletList"),
          () => editor.chain().focus().toggleBulletList().run(),
          labels.bulletList,
          "•",
        )}
        {btn(
          editor.isActive("orderedList"),
          () => editor.chain().focus().toggleOrderedList().run(),
          labels.orderedList,
          "1.",
        )}
        <span className="dn-corr-rte-sep" aria-hidden="true" />
        <label className="dn-corr-rte-size">
          <span className="dn-sr-only">{labels.fontSize}</span>
          <select
            disabled={disabled}
            aria-label={labels.fontSize}
            value={
              FONT_SIZES.find((s) => editor.isActive("textStyle", { fontSize: s })) ?? "11px"
            }
            onChange={(ev) => {
              const size = ev.target.value;
              editor.chain().focus().setFontSize(size).run();
            }}
          >
            {FONT_SIZES.map((s) => (
              <option key={s} value={s}>
                {s.replace("px", "")}
              </option>
            ))}
          </select>
        </label>
        <span className="dn-corr-rte-sep" aria-hidden="true" />
        {btn(false, () => editor.chain().focus().undo().run(), labels.undo, "↶")}
        {btn(false, () => editor.chain().focus().redo().run(), labels.redo, "↷")}
      </div>
      <EditorContent editor={editor} className="dn-corr-rte-surface dn-corr-rich" />
    </div>
  );
}
